import { useMemo } from 'react';
import { useToolbarContextOrNull } from '../context/ToolbarContext';

export interface ToolbarBadges {
  unreadCount: number;
  /** Overdue + pending task count (from lightweight counts endpoint) */
  taskCount: number;
  taskBadgeSeverity: 'danger' | 'warning' | 'secondary';
  activityBadgeCount: number;
  activityBadgeSeverity: 'danger' | 'warning' | 'secondary';
  overdueCheckInCount: number;
  statusBadgeSeverity: 'danger' | 'secondary';
}

export const useToolbarBadges = (): ToolbarBadges => {
  const toolbarContext = useToolbarContextOrNull();

  const unreadCount = toolbarContext?.notificationState?.unreadCount ?? 0;
  // Use lightweight counts endpoint (overdue + pending) instead of full task list length
  const taskCounts = toolbarContext?.taskCounts;
  const taskCount = (taskCounts?.overdue ?? 0) + (taskCounts?.pending ?? 0);
  const overdueCheckInCount = toolbarContext?.overdueCheckInCount ?? 0;

  return useMemo(() => {
    const overdueTaskCount = taskCounts?.overdue ?? 0;

    const taskBadgeSeverity = (() => {
      if (overdueTaskCount > 0) return 'danger' as const;
      if (taskCount > 0) return 'warning' as const;
      return 'secondary' as const;
    })();

    const activityBadgeSeverity = (() => {
      if (unreadCount > 0) return 'danger' as const;
      if (overdueTaskCount > 0) return 'danger' as const;
      if (taskCount > 0) return 'warning' as const;
      return 'secondary' as const;
    })();

    return {
      unreadCount,
      taskCount,
      taskBadgeSeverity,
      activityBadgeCount: unreadCount + taskCount,
      activityBadgeSeverity,
      overdueCheckInCount,
      statusBadgeSeverity: overdueCheckInCount > 0 ? 'danger' as const : 'secondary' as const,
    };
  }, [unreadCount, taskCount, taskCounts?.overdue, overdueCheckInCount]);
};
